import { z } from 'zod';
import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { FormLayout } from './Field';
import { Form, FormField, useZodForm } from './Form';
import { Input } from './Input';
import { Grid } from './Layout';

export const addCompanySchema = z.object({
  name: z.string().trim().min(1, 'Company name is required'),
  supportEmail: z
    .string()
    .trim()
    .min(1, 'Support email is required')
    .email('Please enter a valid email address'),
  /** As typed, formatted "(555) 000-0000"; the container strips it to digits. */
  companyPhone: z.string(),
  streetAddress: z.string(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
});

export type AddCompanyValues = z.infer<typeof addCompanySchema>;

export interface AddCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires with the accepted draft — name and email trimmed, the rest as typed. */
  onCreateCompany: (values: AddCompanyValues) => void;
  /** Locks both buttons and relabels Create while the request runs. */
  submitting?: boolean;
}

const EMPTY: AddCompanyValues = {
  name: '',
  supportEmail: '',
  companyPhone: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
};

const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * The "Add Company" dialog on the companies settings panel: name and support
 * email (required), phone formatted as it is typed, and a postal address.
 * The draft lives inside the dialog body, so it is blank each time the
 * dialog opens.
 *
 *   <AddCompanyDialog open={adding} onOpenChange={setAdding}
 *     onCreateCompany={create} submitting={isSubmitting} />
 */
export function AddCompanyDialog({
  open,
  onOpenChange,
  onCreateCompany,
  submitting = false,
}: AddCompanyDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Company</DialogTitle>
        </DialogHeader>
        <AddCompanyForm
          onCreateCompany={onCreateCompany}
          onCancel={() => onOpenChange(false)}
          submitting={submitting}
        />
      </DialogContent>
    </Dialog>
  );
}

function AddCompanyForm({
  onCreateCompany,
  onCancel,
  submitting,
}: {
  onCreateCompany: (values: AddCompanyValues) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const form = useZodForm(addCompanySchema, { defaultValues: EMPTY });

  return (
    <>
      <Form
        form={form}
        gap={4}
        id="new-company-form"
        onSubmit={onCreateCompany}
      >
        <Grid gap={4}>
          <FormField
            form={form}
            name="name"
            label="Company Name"
            density="relaxed"
          >
            {(field) => <Input {...field} placeholder="Acme Towing" />}
          </FormField>

          <FormField
            form={form}
            name="supportEmail"
            label="Support Email"
            density="relaxed"
          >
            {(field) => (
              <Input
                type="email"
                {...field}
                placeholder="support@acmetowing.com"
              />
            )}
          </FormField>

          <FormField form={form} name="companyPhone" label="Phone">
            {(field) => (
              <Input
                type="tel"
                {...field}
                onChange={(event) =>
                  field.onChange(formatPhoneNumber(event.target.value))
                }
                placeholder="(555) 000-0000"
              />
            )}
          </FormField>

          <FormField form={form} name="streetAddress" label="Street Address">
            {(field) => <Input {...field} placeholder="123 Main St" />}
          </FormField>

          <FormLayout columns={3} gap="compact">
            <FormField form={form} name="city" label="City">
              {(field) => <Input {...field} placeholder="Springfield" />}
            </FormField>
            <FormField form={form} name="state" label="State">
              {(field) => <Input {...field} placeholder="IL" maxLength={2} />}
            </FormField>
            <FormField form={form} name="zipCode" label="Zip Code">
              {(field) => <Input {...field} placeholder="62701" />}
            </FormField>
          </FormLayout>
        </Grid>
      </Form>

      {/* Outside the form so DialogContent can hoist it clear of the
          scrolling body; the submit button reaches the form by id. */}
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button form="new-company-form" type="submit" disabled={submitting}>
          {submitting ? 'Creating...' : 'Create Company'}
        </Button>
      </DialogFooter>
    </>
  );
}
